<template>
  <div class="scorecard-tab">
    <div v-if="provisional" class="banner banner-warn" data-test="provisional-banner">
      <strong>Provisional thresholds. </strong>
      RAG colors are based on default cutoffs and not yet calibrated against this team's history.
    </div>
    <div v-if="dataQualityNotes.length" class="banner banner-info" data-test="dq-banner">
      <strong>Data quality notes. </strong>
      <span>{{ dataQualityNotes.join(" · ") }}</span>
    </div>

    <div class="chip-row">
      <span v-if="sprint.computed_at" class="chip mono" data-test="computed-at">
        {{ formattedComputedAt }}
      </span>
      <span v-if="sprint.job_id" class="chip mono" data-test="job-id">
        job {{ sprint.job_id.slice(0, 8) }}
      </span>
      <span v-if="provisional" class="chip provisional">Provisional thresholds</span>
    </div>

    <div class="md" v-html="markdownHtml" data-test="scorecard-md" />
  </div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import type { SprintDetail } from "@/api/types";
import { renderMarkdown } from "@/lib/markdown";

const props = defineProps<{ sprint: SprintDetail }>();

const provisional = computed(() => {
  const meta = (props.sprint.metrics?._meta ?? {}) as Record<string, unknown>;
  return props.sprint.provisional_thresholds === true || meta.provisional_thresholds === true;
});

const dataQualityNotes = computed<string[]>(() => {
  const dq = props.sprint.data_quality_flags;
  if (Array.isArray(dq)) return dq.filter((x) => typeof x === "string") as string[];
  return [];
});

const formattedComputedAt = computed(() => {
  const v = props.sprint.computed_at;
  if (!v) return "";
  try {
    return new Date(v).toLocaleString();
  } catch {
    return v;
  }
});

const markdownHtml = computed(() => {
  const md = props.sprint.scorecard_markdown || "_No scorecard markdown produced for this sprint._";
  return renderMarkdown(md);
});
</script>

<style scoped>
.scorecard-tab { display: flex; flex-direction: column; gap: 14px; }
.banner {
  display: flex; gap: 8px; padding: 10px 14px; border-radius: 8px; line-height: 1.4;
  border: 1px solid var(--surface-border, #e5e7eb);
}
.banner-warn { background: #fff7ed; border-color: #fdba74; color: #9a3412; }
.banner-info { background: #eff6ff; border-color: #93c5fd; color: #1e40af; }
.chip-row { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 6px; }
.chip {
  display: inline-flex; align-items: center; padding: 2px 8px; border-radius: 999px;
  background: var(--surface-section, #f3f4f6); font-size: 12px;
  border: 1px solid var(--surface-border, #e5e7eb);
}
.chip.mono { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
.chip.provisional { background: #fff7ed; color: #9a3412; border-color: #fdba74; }
.md :deep(h1), .md :deep(h2), .md :deep(h3) { margin: 18px 0 8px; }
.md :deep(table) { width: 100%; border-collapse: collapse; margin: 12px 0; }
.md :deep(th), .md :deep(td) { padding: 6px 10px; border-bottom: 1px solid var(--surface-border, #e5e7eb); }
.md :deep(code) { background: var(--surface-section, #f3f4f6); padding: 1px 4px; border-radius: 4px; }
.md :deep(pre) { background: var(--surface-section, #f3f4f6); padding: 12px; border-radius: 6px; overflow: auto; }
.md :deep(blockquote) {
  border-left: 3px solid var(--surface-border, #e5e7eb);
  padding-left: 12px; color: var(--text-muted, #6b7280);
  margin: 8px 0;
}
</style>
